import { memo, useCallback, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, X } from "lucide-react";
import { clientService } from "@/features/clients/services/client.service";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { formatCPF, formatPhone } from "@/shared/lib/formatters";
import type { NewClientData } from "../../utils/rental-create-payload";

interface ClientPickerFieldProps {
  selectedClientId: number | null;
  selectedClientLabel: string | null;
  newClient: NewClientData | null;
  onSelectExisting: (client: ClientResponseDTO) => void;
  onCreateNew: () => void;
  onClear: () => void;
  error?: string;
}

const SEARCH_DEBOUNCE_MS = 300;

export const ClientPickerField = memo(function ClientPickerField({
  selectedClientId,
  selectedClientLabel,
  newClient,
  onSelectExisting,
  onCreateNew,
  onClear,
  error,
}: ClientPickerFieldProps) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ClientResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const fetchClients = useCallback(
    async (rawTerm: string, signal: AbortSignal) => {
      const trimmed = rawTerm.trim();
      const isDigits = /^\d+$/.test(trimmed);
      const response = await clientService.search(
        isDigits
          ? { document: trimmed, size: 10 }
          : { name: trimmed, size: 10 },
        { signal },
      );
      setShowResults(true);
      return response.content;
    },
    [],
  );

  useDebouncedSearch({
    term,
    enabled: !selectedClientId && !newClient,
    fetcher: fetchClients,
    setItems: setResults,
    setLoading: setSearching,
    isValid: (t) => t.trim().length >= 2,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar clientes")),
    debounceMs: SEARCH_DEBOUNCE_MS,
  });

  if (selectedClientId && selectedClientLabel) {
    return (
      <SelectedCard
        title="Cliente"
        primary={selectedClientLabel}
        onClear={onClear}
      />
    );
  }

  if (newClient) {
    return (
      <SelectedCard
        title="Cliente (novo)"
        primary={newClient.name}
        secondary={`${formatCPF(newClient.document)} · ${formatPhone(newClient.phone)}`}
        onClear={onClear}
      />
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="client-search">Cliente *</Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="client-search"
            placeholder="Busque por nome ou CPF/CNPJ"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            className="pl-8"
          />
          {searching ? (
            <Spinner className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4" />
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={onCreateNew}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Cadastrar novo
        </Button>
      </div>

      {showResults && results.length > 0 ? (
        <div className="rounded-md border border-input bg-background max-h-64 overflow-y-auto">
          {results.map((client) => (
            <button
              type="button"
              key={client.id}
              onClick={() => {
                onSelectExisting(client);
                setTerm("");
                setShowResults(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-accent border-b border-border last:border-b-0"
            >
              <div className="text-sm font-medium">{client.name}</div>
              <div className="text-xs text-muted-foreground">
                {formatCPF(client.document)} · {formatPhone(client.phone)}
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {showResults && !searching && results.length === 0 && term.length >= 2 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum cliente encontrado.
        </p>
      ) : null}

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
});

interface SelectedCardProps {
  title: string;
  primary: string;
  secondary?: string;
  onClear: () => void;
}

function SelectedCard({
  title,
  primary,
  secondary,
  onClear,
}: SelectedCardProps) {
  return (
    <div className="space-y-1">
      <Label>{title} *</Label>
      <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{primary}</div>
          {secondary ? (
            <div className="text-xs text-muted-foreground truncate">
              {secondary}
            </div>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClear}
          aria-label="Trocar cliente"
        >
          <X className="w-4 h-4" />
          <span className="ml-1">Trocar</span>
        </Button>
      </div>
    </div>
  );
}
